import React from 'react';
import '../styles/qualifications.css';
import {
    QUALIFICATION_OPTIONS,
    SUBJECT_GRADE_LEVELS,
    PERCENTAGE_LEVELS,
    CGPA_LEVELS,
    OTHER_LEVEL,
    A_LEVEL_TYPES,
    emptyQualificationsState,
    createEmptyQualificationDetail,
    toggleALevelType,
    changeSubjectRow,
    addSubjectRowToDetail,
    removeSubjectRowFromDetail,
    changeALevelStageField,
    hasLegacyALevelFields
} from '../constants/leadQualifications';

export default function QualificationFields({
    qualifications,
    onChange,
    disabled = false,
    readOnly = false,
    showDuration = false,
    prompt = 'Which qualification have you done?'
}) {
    const state = qualifications || emptyQualificationsState();
    const locked = disabled || readOnly;

    const patchDetail = (level, updater) => {
        onChange({
            ...state,
            details: {
                ...state.details,
                [level]: updater(state.details?.[level] || {})
            }
        });
    };

    const handleToggle = (level) => {
        if (locked) return;
        const selected = state.selected || [];
        const isSelected = selected.includes(level);
        const nextSelected = isSelected
            ? selected.filter(item => item !== level)
            : [...selected, level];
        const details = { ...(state.details || emptyQualificationsState().details) };
        if (!isSelected) details[level] = createEmptyQualificationDetail(level);
        onChange({ selected: nextSelected, details });
    };

    const renderDurationFields = (detail, onFrom, onTo) => {
        if (!showDuration) return null;
        return (
            <div className="qualification-duration-row">
                <div className="qualification-simple-field">
                    <label>Duration From (Month &amp; Year)</label>
                    <input
                        type="month"
                        className="input-field form-control date-input"
                        value={detail?.durationFrom || ''}
                        onChange={(e) => onFrom(e.target.value)}
                        disabled={locked}
                        readOnly={readOnly}
                    />
                </div>
                <div className="qualification-simple-field">
                    <label>Duration To (Month &amp; Year)</label>
                    <input
                        type="month"
                        className="input-field form-control date-input"
                        value={detail?.durationTo || ''}
                        onChange={(e) => onTo(e.target.value)}
                        disabled={locked}
                        readOnly={readOnly}
                    />
                </div>
            </div>
        );
    };

    const renderSubjectTable = (level, subjects, stage) => (
        <div className="qualification-table">
            <div className="qualification-header qualification-header-subjects">
                <span>Subject</span>
                <span>Grade</span>
                <span></span>
            </div>
            {(subjects || []).map((row, index) => (
                <div key={index} className="qualification-row qualification-row-subjects">
                    <input
                        type="text"
                        placeholder="e.g. Mathematics"
                        value={row.subject || ''}
                        onChange={(e) => patchDetail(level, detail => changeSubjectRow(detail, index, 'subject', e.target.value, stage))}
                        disabled={locked}
                        readOnly={readOnly}
                    />
                    <input
                        type="text"
                        placeholder="e.g. A*"
                        value={row.grade || ''}
                        onChange={(e) => patchDetail(level, detail => changeSubjectRow(detail, index, 'grade', e.target.value, stage))}
                        disabled={locked}
                        readOnly={readOnly}
                    />
                    {!readOnly && (
                        <button
                            type="button"
                            className="qualification-remove-btn"
                            onClick={() => patchDetail(level, detail => removeSubjectRowFromDetail(detail, index, stage))}
                            disabled={locked}
                        >
                            ×
                        </button>
                    )}
                </div>
            ))}
            {!readOnly && (
                <button
                    type="button"
                    className="add-subject-btn no-print"
                    onClick={() => patchDetail(level, detail => addSubjectRowToDetail(detail, stage))}
                    disabled={locked}
                >
                    + Add Subject
                </button>
            )}
        </div>
    );

    const renderEducationFields = (level, detail, stage) => (
        <>
            <div className="qualification-simple-field qualification-institute-field">
                <label>School/College/Institute Name</label>
                <input
                    type="text"
                    className="input-field form-control"
                    placeholder="Enter institute name for this qualification"
                    value={detail?.instituteName || ''}
                    onChange={(e) => stage
                        ? patchDetail(level, current => changeALevelStageField(current, stage, 'instituteName', e.target.value))
                        : patchDetail(level, current => ({ ...current, instituteName: e.target.value }))}
                    disabled={locked}
                    readOnly={readOnly}
                />
            </div>
            {renderDurationFields(
                detail,
                (value) => stage
                    ? patchDetail(level, current => changeALevelStageField(current, stage, 'durationFrom', value))
                    : patchDetail(level, current => ({ ...current, durationFrom: value })),
                (value) => stage
                    ? patchDetail(level, current => changeALevelStageField(current, stage, 'durationTo', value))
                    : patchDetail(level, current => ({ ...current, durationTo: value }))
            )}
            {renderSubjectTable(level, detail?.subjects, stage)}
        </>
    );

    return (
        <div className="qualification-fields">
            <p className="qualification-prompt">{prompt}</p>
            <div className="countries-checkbox-grid qualification-levels-grid">
                {QUALIFICATION_OPTIONS.map(level => {
                    const isSelected = (state.selected || []).includes(level);
                    return (
                        <label
                            key={level}
                            className={`country-checkbox-item${isSelected ? ' selected' : ''}${readOnly ? ' read-only' : ''}`}
                        >
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggle(level)}
                                disabled={locked}
                            />
                            <span>{level}</span>
                        </label>
                    );
                })}
            </div>

            {(state.selected || []).map(level => {
                const detail = state.details?.[level] || {};
                const aLevelTypes = level === 'A Level' ? (detail.types || []) : [];
                const showLegacyALevel = level === 'A Level' && aLevelTypes.length === 0 && hasLegacyALevelFields(detail);
                return (
                    <div key={level} className="qualification-detail-box">
                        <h4 className="qualification-detail-title">{level}</h4>

                        {level === 'A Level' && (
                            <div className="a-level-type-block">
                                {/* <p className="qualification-prompt">Is this AS, A2, or both?</p> */}
                                <div className="countries-checkbox-grid a-level-type-grid">
                                    {A_LEVEL_TYPES.map(type => {
                                        const isSelected = aLevelTypes.includes(type);
                                        return (
                                            <label
                                                key={type}
                                                className={`country-checkbox-item${isSelected ? ' selected' : ''}${readOnly ? ' read-only' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => patchDetail('A Level', current => toggleALevelType(current, type))}
                                                    disabled={locked}
                                                />
                                                <span>{type}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {level === 'A Level' && aLevelTypes.map(type => (
                            <div key={type} className="a-level-stage-box">
                                <h5 className="a-level-stage-title">{type}</h5>
                                {renderEducationFields(level, detail.stages?.[type], type)}
                            </div>
                        ))}

                        {level === 'A Level' && showLegacyALevel && renderEducationFields(level, detail)}

                        {level !== 'A Level' && level !== OTHER_LEVEL && (
                            <>
                                <div className="qualification-simple-field qualification-institute-field">
                                    <label>School/College/Institute Name</label>
                                    <input
                                        type="text"
                                        className="input-field form-control"
                                        placeholder="Enter institute name for this qualification"
                                        value={detail.instituteName || ''}
                                        onChange={(e) => patchDetail(level, current => ({ ...current, instituteName: e.target.value }))}
                                        disabled={locked}
                                        readOnly={readOnly}
                                    />
                                </div>
                                {renderDurationFields(
                                    detail,
                                    (value) => patchDetail(level, current => ({ ...current, durationFrom: value })),
                                    (value) => patchDetail(level, current => ({ ...current, durationTo: value }))
                                )}
                            </>
                        )}

                        {level !== 'A Level' && SUBJECT_GRADE_LEVELS.includes(level) && (
                            renderSubjectTable(level, detail.subjects)
                        )}

                        {PERCENTAGE_LEVELS.includes(level) && (
                            <div className="qualification-simple-field">
                                <label>Percentage</label>
                                <input
                                    type="text"
                                    className="input-field form-control"
                                    placeholder="e.g. 85%"
                                    value={detail.percentage || ''}
                                    onChange={(e) => patchDetail(level, current => ({ ...current, percentage: e.target.value }))}
                                    disabled={locked}
                                    readOnly={readOnly}
                                />
                            </div>
                        )}

                        {CGPA_LEVELS.includes(level) && (
                            <div className="qualification-simple-field">
                                <label>CGPA / Division</label>
                                <input
                                    type="text"
                                    className="input-field form-control"
                                    placeholder="e.g. 3.5 / First Division"
                                    value={detail.cgpaDivision || ''}
                                    onChange={(e) => patchDetail(level, current => ({ ...current, cgpaDivision: e.target.value }))}
                                    disabled={locked}
                                    readOnly={readOnly}
                                />
                            </div>
                        )}

                        {level === OTHER_LEVEL && (
                            <div className="qualification-simple-field">
                                <label>Name / Grade</label>
                                <input
                                    type="text"
                                    className="input-field form-control"
                                    placeholder="Enter qualification name or grade"
                                    value={detail.nameOrGrade || ''}
                                    onChange={(e) => patchDetail(level, current => ({ ...current, nameOrGrade: e.target.value }))}
                                    disabled={locked}
                                    readOnly={readOnly}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
